import React, { useState, useMemo } from 'react';
import {
  SWIFT_CSCF_2025_CONTROLS_CATALOG,
  getSwiftCscfControlsByDomain,
  getSwiftCscfControlDomains,
  type SwiftCscfControl,
  type SwiftCscfControlStatus,
} from '../../data/swiftCscf2025ControlsCatalog';
import { computeSwiftCscfControlsScore } from '../../utils/swiftCscfScore';
import { useI18n } from '../../i18n';
import { ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { componentScope } from '../../lib/cssScope'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'

const STATUS_OPTIONS: SwiftCscfControlStatus[] = ['IMPLEMENTED', 'PARTIAL', 'NOT_IMPLEMENTED', 'N_A'];
const STATUS_LABELS: Record<SwiftCscfControlStatus, string> = {
  IMPLEMENTED: 'Implemented',
  PARTIAL: 'Partial',
  NOT_IMPLEMENTED: 'Not Implemented',
  N_A: 'N/A',
};

interface SwiftCscfControlsPanelProps {
  initialStatuses?: Record<string, SwiftCscfControlStatus>;
  onSave?: (statuses: Record<string, SwiftCscfControlStatus>) => Promise<void>;
  disabled?: boolean;
}

export default function SwiftCscfControlsPanel({
  initialStatuses = {},
  onSave,
  disabled = false,
}: SwiftCscfControlsPanelProps) {
  const { t, locale } = useI18n();
  const DOMAIN_ORDER = getSwiftCscfControlDomains();
  const [statuses, setStatuses] = useState<Record<string, SwiftCscfControlStatus>>(initialStatuses);
  React.useEffect(() => {
    if (initialStatuses && Object.keys(initialStatuses).length > 0) {
      setStatuses((prev) => ({ ...prev, ...initialStatuses }));
    }
  }, [initialStatuses]);
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(
    new Set([DOMAIN_ORDER[0] || 'Mandatory'])
  );
  const [filterDomain, setFilterDomain] = useState<SwiftCscfControl['domain'] | 'all'>('all');
  const [isSaving, setIsSaving] = useState(false);
  const handleStatusChange = (code: string, status: SwiftCscfControlStatus | '') => {
    if (disabled) return;
    if (status === '') {
      setStatuses((prev) => {
        const next = { ...prev };
        delete next[code];
        return next;
      });
      return;
    }
    setStatuses((prev) => ({ ...prev, [code]: status }));
  };
  const handleSave = async () => {
    if (!disabled && !isSaving && onSave) {
      setIsSaving(true);
      try {
        await onSave(statuses);
      } finally {
        setIsSaving(false);
      }
    }
  };
  const toggleDomain = (d: string) =>
    setExpandedDomains((prev) => {
      const n = new Set(prev);
      n.has(d) ? n.delete(d) : n.add(d);
      return n;
    });
  const controlsByDomain = useMemo(
    () =>
      filterDomain === 'all'
        ? DOMAIN_ORDER.flatMap((d) => getSwiftCscfControlsByDomain(d))
        : getSwiftCscfControlsByDomain(filterDomain),
    [filterDomain, DOMAIN_ORDER]
  );
  const stats = useMemo(() => {
    const { score, mandatoryScore, advisoryScore, breakdown } = computeSwiftCscfControlsScore(statuses);
    return { total: SWIFT_CSCF_2025_CONTROLS_CATALOG.length, ...breakdown, score, mandatoryScore, advisoryScore };
  }, [statuses]);
  const getTitle = (c: SwiftCscfControl) =>
    locale === 'ru' ? c.title.ru : locale === 'es' ? c.title.es : c.title.en;
  const getRec = (c: SwiftCscfControl) => {
    const r = c.recommendations.CRITICAL || c.recommendations.MAJOR;
    return r ? (locale === 'ru' ? r.ru : locale === 'es' ? r.es : r.en) : null;
  };

  return (
    <div className={`${componentScope('SwiftCscfControlsPanel')} space-y-4`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('compliance.swiftCscf.controls.title', 'SWIFT CSCF 2025 Controls Catalog')}
          </h3>
          <p className="text-sm text-gray-500">
            {t('compliance.swiftCscf.controls.subtitle', '16 controls • 7 Mandatory, 9 Advisory')}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Select<SwiftCscfControl['domain'] | 'all'>
            options={[
              { value: 'all', label: t('compliance.swiftCscf.controls.allDomains', 'All domains') },
              ...DOMAIN_ORDER.map((d) => ({ value: d, label: d })),
            ]}
            value={filterDomain}
            onChange={(v) => v != null && setFilterDomain(v)}
            placeholder={t('compliance.swiftCscf.controls.allDomains', 'All domains')}
            minWidth="12rem"
          />
          {onSave && (
            <Button type="button" color="primary" size="small" onClick={handleSave} disabled={disabled || isSaving}>
              {isSaving ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
            </Button>
          )}
        </div>
      </div>
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
        <div className="flex flex-wrap gap-6 text-sm">
          <span><strong>{stats.implemented}</strong> {t('compliance.swiftCscf.controls.implemented', 'Implemented')}</span>
          <span><strong>{stats.partial}</strong> {t('compliance.swiftCscf.controls.partial', 'Partial')}</span>
          <span><strong>{stats.notImplemented}</strong> {t('compliance.swiftCscf.controls.notImplemented', 'Not Implemented')}</span>
          <span><strong>{stats.na}</strong> N/A</span>
          {typeof stats.mandatoryScore === 'number' && (
            <span className="text-red-700 dark:text-red-400">Mandatory: <strong>{stats.mandatoryScore}%</strong></span>
          )}
          {typeof stats.advisoryScore === 'number' && (
            <span className="text-amber-700 dark:text-amber-400">Advisory: <strong>{stats.advisoryScore}%</strong></span>
          )}
          <span className="ml-auto font-medium">{t('compliance.swiftCscf.controls.score', 'Score')}: <strong>{stats.score}%</strong></span>
        </div>
      </div>
      <div className="space-y-2">
        {(filterDomain === 'all' ? DOMAIN_ORDER : [filterDomain]).map((domain) => {
          const domainControls = filterDomain === 'all' ? getSwiftCscfControlsByDomain(domain) : controlsByDomain;
          const isExpanded = expandedDomains.has(domain);
          return (
            <div key={domain} className="rounded-lg border border-gray-200 dark:border-gray-700">
              {filterDomain === 'all' && (
                <Button
                  type="button"
                  color="outline"
                  onClick={() => toggleDomain(domain)}
                  className="w-full !justify-between rounded-none !border-x-0 !border-t-0 !border-b-gray-200 !bg-transparent !px-4 !py-3 !text-left !font-medium !text-gray-900 !shadow-none dark:!border-b-gray-700 dark:!text-gray-100"
                >
                  <span className="flex items-center gap-2">
                    {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    {domain} ({getSwiftCscfControlsByDomain(domain).length})
                  </span>
                </Button>
              )}
              {(filterDomain !== 'all' || isExpanded) && domainControls.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-700">
                  {domainControls.map((control) => (
                    <div
                      key={control.controlCode}
                      className="border-b border-gray-100 last:border-b-0 dark:border-gray-700/50"
                    >
                      <div className="flex flex-wrap items-start gap-4 px-4 py-3">
                        <div className="min-w-0 flex-1">
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {control.controlCode.replace('SWIFT_', '')}: {getTitle(control)}
                            {control.controlType === 'mandatory' && (
                              <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">M</span>
                            )}
                          </span>
                        </div>
                        <Select<string>
                          options={[
                            { value: '', label: '—' },
                            ...STATUS_OPTIONS.map((s) => ({ value: s, label: STATUS_LABELS[s] })),
                          ]}
                          value={statuses[control.controlCode] ?? ''}
                          onChange={(v) =>
                            handleStatusChange(control.controlCode, (v ?? '') as SwiftCscfControlStatus | '')
                          }
                          disabled={disabled}
                          minWidth="9rem"
                        />
                      </div>
                      {(statuses[control.controlCode] === 'PARTIAL' || statuses[control.controlCode] === 'NOT_IMPLEMENTED') && getRec(control) && (
                        <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-2 text-sm dark:border-gray-700/50 dark:bg-gray-800/30">
                          <div className="flex items-start gap-2 rounded-md bg-amber-50 p-2 dark:bg-amber-900/20">
                            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
                            <p className="text-amber-800 dark:text-amber-200">{getRec(control)}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
